import type { ComponentPropsWithoutRef } from 'react';
import type { AvatarData } from '@/api/gen/_model/avatarData';
import { cn } from '@/utils/tailwind.util';
import { AvatarSpriteLayers } from '../avatarSpriteLayers';

/**
 * 전신 아바타 렌더러.
 *
 * `SpriteAvatar` 는 `overflow-hidden rounded-full` + 상단 오프셋으로 머리만 원형
 * 크롭하는 썸네일이라 카드 프리뷰엔 부적합하다. 이 컴포넌트는 동일한
 * 스프라이트 시트에서 **프레임 전체(48×64, 전신)** 를 잘리지 않게 보여준다.
 *
 * 파츠 레이어 조합(순서/URL/정규화)은 `avatarSpriteLayers` 로 공통화했고, 여기선
 * 전신 프레임 크롭(시트 확대)만 담당한다.
 *
 * 시트: 432×256 = 9열 × 4행, 프레임 48×64. 첫 프레임(col0,row0) = 정면 stand.
 * ponytail: 정지 프레임(stand)만 렌더. walk/dance 애니메이션은 play-app `Avatar`
 * 의 프레임/방향 매핑이 필요해 후속 작업으로 분리.
 */
const FRAME_WIDTH = 48;
const FRAME_HEIGHT = 64;
const SHEET_COLS = 9;
const SHEET_ROWS = 4;
export const FullBodyAvatar = ({
  avatarData,
  pixelHeight = 128,
  className,
  style,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  avatarData: AvatarData;
  /** 렌더 높이(px). 너비는 프레임 비율(48:64)로 자동 계산. 기본 128. */
  pixelHeight?: number;
}) => {
  const height = pixelHeight;
  const width = Math.round(pixelHeight * FRAME_WIDTH / FRAME_HEIGHT);
  const layerStyle = {
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '0 0',
    // 시트를 컨테이너(=프레임 1개) 기준으로 확대 → 좌상단 프레임만 보임.
    backgroundSize: `${width * SHEET_COLS}px ${height * SHEET_ROWS}px`,
    imageRendering: 'pixelated' as const
  };
  return <div className={cn('relative shrink-0', className)} style={{
    width,
    height,
    ...style
  }} {...props} data-sentry-component="FullBodyAvatar" data-sentry-source-file="FullBodyAvatar.tsx">
      <AvatarSpriteLayers avatarData={avatarData} layerStyle={layerStyle} data-sentry-element="AvatarSpriteLayers" data-sentry-source-file="FullBodyAvatar.tsx" />
    </div>;
};